// src/pages/vendas/ModalFinalizarVenda.js
import React, { useCallback, useEffect, useRef, useState } from 'react';
import Input from '../../components/input/Input';
import "./ModalFinalizarVenda.css";
import { useCliente } from '../../contexts/pessoas/ClienteContext';
import { useFormaPag } from '../../contexts/financeiro/FormaPagContext.js';
import { useAlert } from '../../contexts/geral/AlertContext';
import {
    formatCurrency,
    formatNumeric,
    fromMoneyCents,
    normalizeMoney,
    normalizeDecimalInputBr,
    parseNumeric,
    parseCurrency,
    toMoneyCents
} from '../../utils/numberUtils.js';
import { NotaPag } from '../../models/vendas/NotaPag.js';
import Select from '../../components/input/Select.js';
import ModalContainer from '../../components/modalForm/ModalContainer';
import ModalHeader from '../../components/modalForm/ModalHeader';
import ModalBody from '../../components/modalForm/ModalBody';
import ModalFooter from '../../components/modalForm/ModalFooter';
import ModalTable from '../../components/modalForm/ModalTable';
import { ModalActionButton } from '../../components/modalForm/ModalActions';
import { useMessage } from '../../contexts/geral/MessageContext.js';
import { useNota } from '../../contexts/vendas/NotaContext.js';
import { usePrinter } from '../../contexts/geral/PrinterContext.js';
import { useYesNo } from '../../contexts/geral/YesNoContext.js';
import ModalTefManual from './ModalTefManual.js';
import ModalEditarItensNota from './ModalEditarItensNota.js';
import { useConfigTEF } from '../../contexts/administrativo/ConfigTEFContext.js';
import { ConfigTEF } from '../../models/administrativo/ConfigTEF.ts';
import { FaCreditCard, FaMobileAlt, FaKeyboard, FaTrashAlt, FaEdit } from "react-icons/fa";
import { FiUser, FiList, FiDollarSign, FiArrowDownLeft, FiCornerUpLeft } from "react-icons/fi";
import { useNotaPag } from '../../contexts/vendas/NotaPagContext.js';
import FormaPag, { MeioPagamento } from '../../models/financeiro/FormaPag.js';
import { useConfigModulo } from '../../contexts/administrativo/ConfigModuloContext.js';
import { DFePadraoEnum, permiteEmissaoNotaProduto } from '../../models/administrativo/ConfigModulo.js';
import {
    deveProcessarTEF,
    pagamentoBloqueiaTerminal,
    obterTerminalRegistradoVenda,
    prepararPagamentosTerminal,
    terminalSemTef,
    terminalVendaBloqueado,
    validarTerminalVenda
} from '../../services/TerminalPagamentoService';
import { useNatureza } from '../../contexts/vendas/NaturezaContext.js';
import {
    calcularTotalPagoPDV,
    calcularValorNotaPDV,
    classificarItensFiscaisPDV,
    deveEmitirNotaFiscalPDV,
    deveMostrarTerminalPDV,
    emitirNFCePDV,
    emitirNFePDV,
    emitirVendaFiscalPDV,
    aguardarPagamentoMonitorTEF,
    aguardarPagamentoPOSPDV,
    atualizarFinalizacaoNotaPDV,
    buscarStatusPagamentoMonitorTEFPDV,
    deveSolicitarPagamentoMonitorTEF,
    inicializarClienteFinalizacaoPDV,
    inicializarPagamentosFinalizacaoPDV,
    obterStatusTEFMonitorPDV,
    montarPayloadNovaNotaPDV,
    solicitarImpressaoNFCeMonitorDesktopPDV,
    solicitarImpressaoNFSeMonitorPDV,
    solicitarImpressaoReciboMonitorDesktopPDV,
    solicitarPagamentoMonitorTEFPDV,
    solicitarPagamentoPOSPDV,
    IntegradorTEFEnum,
    integradorTefDisponivelPDV,
    validarEnderecoClienteFretePDV
} from '../../services/vendas/PDVService.js';

const getErrorMessage = (error) => error?.message || error?.title || String(error);

const formatarValorInput = (valor) => toMoneyCents(valor) === 0 ? null : formatNumeric(valor);

const formatarDataInput = (data) => {
    if (!data) {
        return '';
    }

    if (typeof data === 'string') {
        return data.substring(0, 10);
    }

    const dataLocal = new Date(data);
    const ano = dataLocal.getFullYear();
    const mes = String(dataLocal.getMonth() + 1).padStart(2, '0');
    const dia = String(dataLocal.getDate()).padStart(2, '0');
    return `${ano}-${mes}-${dia}`;
};

const obterPrimeiroVencimentoPadrao = (formaPag) => {
    const diaVencimento = Number(formaPag?.diaVencimento);
    if (!Number.isInteger(diaVencimento) || diaVencimento <= 0) {
        return '';
    }

    const hoje = new Date();
    const ano = hoje.getFullYear();
    const mesSeguinte = hoje.getMonth() + 1;
    const ultimoDiaMesSeguinte = new Date(ano, mesSeguinte + 1, 0).getDate();
    const dia = Math.min(diaVencimento, ultimoDiaMesSeguinte);

    return formatarDataInput(new Date(ano, mesSeguinte, dia));
};

const aplicarCondicoesPagamento = (notaPag, formaPag) => {
    if (!formaPag || formaPag.avista === true) {
        return { ...notaPag, qtdeParcela: null, dataVencimento: null };
    }

    return {
        ...notaPag,
        qtdeParcela: Number(notaPag?.qtdeParcela) > 0 ? Number(notaPag.qtdeParcela) : Number(formaPag.qtdeParcela) || 1,
        dataVencimento: formatarDataInput(notaPag?.dataVencimento) || obterPrimeiroVencimentoPadrao(formaPag)
    };
};

const ModalFinalizarVenda = ({ show, onHide, nota, setNota, onConfirm, permitirEditarItens = false }) => {
    const inicializacaoFinalizacaoRef = useRef(false);
    const confirmarRef = useRef(null);
    const { configModuloSel, dFeSelecionadoSessao, setDFeSelecionadoSessao } = useConfigModulo();
    const { clientes, showClienteSearch, showClienteCad } = useCliente();
    const { naturezas, handleShowNaturezaSearch } = useNatureza();
    const { formasPag } = useFormaPag();
    const { configTEFList } = useConfigTEF();
    const [configTEFSel, setConfigTEFSel] = useState(new ConfigTEF());
    const { showAlert } = useAlert();
    const {showMessage} = useMessage();
    const { showYesNo } = useYesNo(); // Usar o contexto YesNo
    const { addNota } = useNota(); // Use NotaContext
    const { fetchNotaPags, deleteNotaPag, notaPagSel, setNotaPagSel, notaPags, setNotaPags, aplicarPagamentosFinalizacao } = useNotaPag();
    const pagamentosRef = useRef([]);
    const pagamentoSelecionadoRef = useRef(null);
    const recebidoRef = useRef(null);
    const notaAtualRef = useRef(nota);
    const operacaoRef = useRef(false);
    const sessaoRef = useRef(0);
    const showRef = useRef(show);
    showRef.current = show;
    notaAtualRef.current = nota;
    const [formaPagSel, setFormaPagSel] = useState(new FormaPag());

    const [isGenerating, setIsGenerating] = useState(false); // Novo estado para controlar o carregamento
    const [mostraTerminal, setMostraTerminal] = useState(false);
    const [aguardandoPagamentoTEF, setAguardandoPagamentoTEF] = useState(false);
    const cancelarEsperaTEFRef = useRef(false);
    

    //TEF MANUAL
    const [showModal, setShowModal] = useState(false);

    // Edição dos itens da nota (NotaItem) antes da finalização da venda.
    // Só faz sentido enquanto a nota ainda não foi persistida (sem id), pois a
    // atualização de itens não é reenviada para notas já criadas.
    const [showEditarItens, setShowEditarItens] = useState(false);

    const handleAbrirEditarItens = () => {
        setShowEditarItens(true);
    };

    const handleFecharEditarItens = () => {
        setShowEditarItens(false);
    };

    const handleSalvarItensEditados = (notaItensAtualizados) => {
        setNota((notaAtual) => ({
            ...notaAtual,
            notaItens: notaItensAtualizados
        }));
        setShowEditarItens(false);
    };

    // Função que mostra o modal e retorna uma Promise
    const solicitarDadosTef = () => {
        return new Promise((resolve) => {
            setShowModal(true);

            const handleResolve = (confirmed, pagamentos) => {
                setShowModal(false);
                if (confirmed && Array.isArray(pagamentos)) sincronizarPagamentos(pagamentos, pagamentoSelecionadoRef.current);
                onResolveRef.current = () => {};
                resolve(confirmed); // ✅ resolve com true ou false
            };

            onResolveRef.current = handleResolve; // passa para o modal
        });
    };

    const onResolveRef = useRef(() => {});

    useEffect(() => {
        cancelarEsperaTEFRef.current = !show;

        return () => {
            sessaoRef.current += 1;
            cancelarEsperaTEFRef.current = true;
            onResolveRef.current(false);
        };
    }, [show]);

    const deveMostrarTerminal = (formaPag) => {
        return deveMostrarTerminalPDV({ formaPag, configTEFList });
    };

    const handlePagamento = async () => {
        const confirmado = await solicitarDadosTef(); // ⬅️ Espera a resposta do modal

        if (confirmado) {
            console.log('✅ Confirmado!');               
            // Aqui você pode prosseguir com o envio dos dados
            return true;
        } else {
            console.log('❌ Cancelado!');
            // Aqui você pode abortar a operação
            return false;
        }
    };

    const registrarStatusTef = (pagamento, statusTEF, pago = statusTEF === 2) => {
        const pagamentos = pagamentosRef.current.map(item => NotaPag.mesmoPagamento(item, pagamento)
            ? { ...item, configTEFid: configTEFSel.id, statusTEF, pago } : item);
        const selecionado = pagamentos.find(item => NotaPag.mesmoPagamento(item, pagamentoSelecionadoRef.current))
            || pagamentos[pagamentos.length - 1];
        aplicarEstadoPagamentos(pagamentos, selecionado);
    };
    
    const { printReciboPDF, printNFCePDFById, printNFSePDFById } = usePrinter();
    const [vRecebido, setVRecebido] = useState(null);
    const [vTroco, setVTroco] = useState(0);
    const [vFreteInput, setVFreteInput] = useState(null);
    const atualizarRecebido = (valor) => {
        const valorInput = valor === '' ? null : valor;
        recebidoRef.current = valorInput;
        setVRecebido(valorInput);
    };
    const aplicarEstadoPagamentos = (pagamentos, selecionado) => {
        pagamentosRef.current = pagamentos;
        pagamentoSelecionadoRef.current = selecionado;
        if (aplicarPagamentosFinalizacao) aplicarPagamentosFinalizacao(pagamentos, selecionado);
        else {
            setNotaPags(pagamentos);
            setNotaPagSel(selecionado);
        }
        setFormaPagSel(selecionado?.formaPag);
        setMostraTerminal(deveMostrarTerminal(selecionado?.formaPag));
        atualizarRecebido(formatarValorInput(selecionado?.vRec));
        setVTroco(selecionado?.vTroco || 0);
    };
    const completarPagamentosUI = (persistidos) => {
        const pagamentos = [...persistidos];
        if (toMoneyCents(calcularTotalPagoPDV(pagamentos)) < toMoneyCents(notaAtualRef.current.vNota)
            && pagamentos.every(p => toMoneyCents(p.vPag) > 0)) {
            pagamentos.push(pagamentosRef.current.find(p => !p.id && toMoneyCents(p.vPag) === 0)
                || novaNotaPag(NotaPag.proximoItem(pagamentos)));
        }
        return pagamentos;
    };
    const tipoNota = dFeSelecionadoSessao === DFePadraoEnum.NFe ? 'NF-e' : 'NFC-e';
    const emissaoNotaProdutoHabilitada = permiteEmissaoNotaProduto(configModuloSel);

    const obterNaturezaPadraoNFe = useCallback((notaAtual = {}) => {
        return naturezas.find(n => Number(n.id) === 1)
            || (Number(notaAtual?.natureza?.id) === 1 ? notaAtual.natureza : null)
            || { id: 1, descricao: '' };
    }, [naturezas]);

    const handleSelecionarTipoNota = (novoTipoNota) => {
        if (operacaoRef.current || novoTipoNota === tipoNota) return;
        setDFeSelecionadoSessao(
            novoTipoNota === 'NF-e' ? DFePadraoEnum.NFe : DFePadraoEnum.NFCe
        );

        if (novoTipoNota === 'NF-e') {
            setNota((notaAtual) => {
                const naturezaPadrao = obterNaturezaPadraoNFe(notaAtual);

                return {
                    ...notaAtual,
                    naturezaId: 1,
                    natureza: naturezaPadrao
                };
            });
            return;
        }

        if (novoTipoNota === 'NFC-e') {
            setNota((notaAtual) => ({
                ...notaAtual,
                naturezaId: 1,
                natureza: null
            }));
        }
    };

    useEffect(() => {
        if (tipoNota !== 'NF-e' || Number(nota?.naturezaId) !== 1 || nota?.natureza?.descricao) {
            return;
        }

        const naturezaPadrao = obterNaturezaPadraoNFe(nota);
        if (naturezaPadrao?.descricao) {
            setNota((notaAtual) => ({
                ...notaAtual,
                naturezaId: 1,
                natureza: naturezaPadrao
            }));
        }
    }, [tipoNota, naturezas, nota, nota?.naturezaId, nota?.natureza?.descricao, obterNaturezaPadraoNFe]);


    const novaNotaPag = (item = NotaPag.proximoItem(pagamentosRef.current)) => {
        const formasAtivas = formasPag.filter(forma => !forma.inativo);
        const formaPagPadrao = formasAtivas.find(
            (formaPag) => Number(formaPag.meioPag) === MeioPagamento.Dinheiro
        ) || formasAtivas[0];
        const notaPag = new NotaPag();
        notaPag.item = item;
        notaPag.formaPagId = formaPagPadrao?.id || 1; //Dinheiro
        notaPag.formaPag = formaPagPadrao || new FormaPag();
        return notaPag;
    };

    const atualizarCondicoesPagamento = (campos) => {
        const selecionado = pagamentoSelecionadoRef.current;
        if (operacaoRef.current || pagamentoBloqueiaTerminal(selecionado)) return;
        const atualizado = { ...selecionado, ...campos };
        aplicarEstadoPagamentos(pagamentosRef.current.map(pagamento =>
            NotaPag.mesmoPagamento(pagamento, selecionado) ? atualizado : pagamento), atualizado);
    };

    const calcularValorNota = calcularValorNotaPDV;

    useEffect(() => {
        if (!show || operacaoRef.current) return;
        setNota(atual => inicializarClienteFinalizacaoPDV({ nota: atual, clientes }));
    }, [show, clientes, setNota]);

    useEffect(() => {
        if (!show) {
            inicializacaoFinalizacaoRef.current = false;
            setConfigTEFSel(new ConfigTEF());
            setVFreteInput(null);
            atualizarRecebido(null);
            setVTroco('0,00');
            setIsGenerating(false);//desabilita o botão processando
            operacaoRef.current = false;
            pagamentosRef.current = [];
            pagamentoSelecionadoRef.current = null;
            onResolveRef.current(false);
            return;
        }

        if (inicializacaoFinalizacaoRef.current || formasPag.length === 0) {
            return;
        }
        inicializacaoFinalizacaoRef.current = true;
        setVFreteInput(formatarValorInput(normalizeMoney(nota?.vFrete)));

        // A inicialização usa somente variáveis locais. Chamar handlers logo
        // após setState faria a nova venda enxergar pagamentos da abertura anterior.
        // O troco começa zerado e só é derivado quando a própria nota informa
        // recebimento em dinheiro; Pix e cartões nunca reutilizam troco legado.
        const pagamentosInicializados = inicializarPagamentosFinalizacaoPDV({ nota, formasPag })
            .map((pagamento) => aplicarCondicoesPagamento(pagamento, pagamento.formaPag));

        if (toMoneyCents(calcularTotalPagoPDV(pagamentosInicializados)) < toMoneyCents(nota.vNota)
            && pagamentosInicializados.every(p => toMoneyCents(p.vPag) > 0)) {
            pagamentosInicializados.push(novaNotaPag(NotaPag.proximoItem(pagamentosInicializados)));
        }
        const ultimaNotaPag = pagamentosInicializados[pagamentosInicializados.length - 1];
        aplicarEstadoPagamentos(pagamentosInicializados, ultimaNotaPag);

        setIsGenerating(false);//desabilita o botão processando
      }, [show, formasPag]);

    useEffect(() => {
        if (!show || !configTEFList.length) return;
        try {
            const registrado = obterTerminalRegistradoVenda(nota?.notaPags || [], configTEFList);
            const disponiveis = configTEFList.filter(integradorTefDisponivelPDV);
            const padrao = disponiveis.find((terminal) => !terminalSemTef(terminal)) || disponiveis[0];
            setConfigTEFSel((atual) => registrado
                || disponiveis.find((terminal) => Number(terminal.id) === Number(atual?.id))
                || padrao || new ConfigTEF());
        } catch (error) {
            showMessage(error.message, 'warning');
        }
    }, [show, configTEFList, nota?.id]);

    useEffect(() => {
        setMostraTerminal(deveMostrarTerminalPDV({ formaPag: formaPagSel, configTEFList }));
    }, [formaPagSel, configTEFList]);

    //   useEffect(() => {
    //     if (formasPag.length && !notaPagSel.formaPagId) {
    //         setNotaPagSel({
    //             ...notaPagSel,
    //             formaPagId: formasPag[0]?.id,
    //             formaPag: formasPag[0]
    //         });
    //     }
    // }, [formasPag]);

    const handleBuscarCliente = async () => {
        const clienteSel = await showClienteSearch();
        console.log('Cliente selecionado:', clienteSel);
        if (clienteSel?.id) {
            handleClienteExit(clienteSel.id, clienteSel);
        }
    };

    const handleAddCliente = async () => {
        const clienteSelecionado = clientes.find(
            f => f.id.toString() === (nota?.clienteId || '').toString()
        );

        let clienteSalvo = null;
        if (Number(nota?.clienteId) !== 1 && clienteSelecionado) {
            clienteSalvo = await showClienteCad(clienteSelecionado, false);
        } else {
            clienteSalvo = await showClienteCad(undefined, false);
        }

        if (clienteSalvo?.id) {
            handleClienteExit(clienteSalvo.id, clienteSalvo);
        }
    };

    const validarEnderecoClienteParaFrete = (notaAtual) => {
        const resultado = validarEnderecoClienteFretePDV({ notaAtual, clientes });
        if (!resultado.valido) showMessage(resultado.mensagem, 'warning');
        return resultado.valido;
    };

    const garantirNotaEPagamentos = async () => {
        const sessao = sessaoRef.current;
        const validarSessao = () => {
            if (!showRef.current || sessaoRef.current !== sessao) throw new Error('A finalização foi interrompida.');
        };
        let notaAtual = tipoNota === 'NFC-e'
            ? { ...notaAtualRef.current, naturezaId: 1, natureza: null } : notaAtualRef.current;
        let pagamentos = prepararPagamentosTerminal(
            pagamentosRef.current.filter(p => toMoneyCents(p.vPag) > 0), configTEFSel);
        if (!pagamentos.length) throw new Error('Informe pelo menos um pagamento.');
        if (!notaAtual.id) {
            const notaSalva = await addNota(montarPayloadNovaNotaPDV({ nota: notaAtual, notaPags: pagamentos }));
            validarSessao();
            if (!notaSalva?.id) throw new Error('O servidor não retornou a nota salva.');
            notaAtual = { ...notaAtual, ...notaSalva };
            notaAtualRef.current = notaAtual;
            setNota(notaAtual);
            // O ID é guardado antes da consulta para impedir outra criação após uma falha de rede.
            if (Array.isArray(notaSalva.notaPags)) sincronizarPagamentos(notaSalva.notaPags, pagamentoSelecionadoRef.current);
            const consultados = await fetchNotaPags(notaAtual.id, { atualizarEstado: false });
            validarSessao();
            if (!Array.isArray(consultados) || !consultados.length) throw new Error('Não foi possível consultar os pagamentos salvos.');
            pagamentos = consultados.map(p => NotaPag.reidratar(p, formasPag,
                pagamentos.find(anterior => NotaPag.mesmoPagamento(p, anterior))));
        } else {
            // Cliente, pagamentos e financeiro são gravados juntos, inclusive a remoção dos zerados.
            const atualizada = await atualizarFinalizacaoNotaPDV(notaAtual, pagamentos);
            validarSessao();
            if (!atualizada?.id || !Array.isArray(atualizada.notaPags) || !atualizada.notaPags.length)
                throw new Error('Não foi possível atualizar a finalização e os pagamentos da nota.');
            pagamentos = atualizada.notaPags.map(p => NotaPag.reidratar(p, formasPag,
                pagamentos.find(anterior => NotaPag.mesmoPagamento(p, anterior))));
            notaAtual = { ...notaAtual, ...atualizada };
            notaAtualRef.current = notaAtual;
            setNota(notaAtual);
        }
        const pagamentosUI = completarPagamentosUI(pagamentos);
        const selecionado = pagamentosUI.find(p => NotaPag.mesmoPagamento(p, pagamentoSelecionadoRef.current))
            || pagamentosUI[pagamentosUI.length - 1];
        aplicarEstadoPagamentos(pagamentosUI, selecionado);
        const sincronizada = { ...notaAtual, notaPags: pagamentos };
        notaAtualRef.current = sincronizada;
        setNota(sincronizada);
        return sincronizada;
    };

    const sincronizarPagamentos = (pagamentosAtualizados, pagamentoReferencia) => {
        if (!Array.isArray(pagamentosAtualizados) || !pagamentosAtualizados.length) return false;
        const pagamentos = pagamentosAtualizados.map(p => NotaPag.reidratar(p, formasPag,
            pagamentosRef.current.find(anterior => NotaPag.mesmoPagamento(p, anterior))));
        const pagamentosUI = completarPagamentosUI(pagamentos);
        let selecionado = pagamentosUI.find(p => NotaPag.mesmoPagamento(p, pagamentoSelecionadoRef.current))
            || pagamentosUI.find(p => NotaPag.mesmoPagamento(p, pagamentoReferencia))
            || pagamentosUI[pagamentosUI.length - 1];
        if (pagamentoBloqueiaTerminal(selecionado)) {
            selecionado = pagamentosUI.find(p => !p.id && toMoneyCents(p.vPag) === 0) || selecionado;
        }
        aplicarEstadoPagamentos(pagamentosUI, selecionado);
        notaAtualRef.current = { ...notaAtualRef.current, notaPags: pagamentos };
        setNota(notaAtualRef.current);
        return true;
    };

    const handleConfirm = async () => {
        if (operacaoRef.current) return;
        const sessao = sessaoRef.current;
        const consolidados = handleExitValorRecebido(recebidoRef.current, undefined, false);
        if (!consolidados) return;
        const pagamentosValidos = consolidados.filter(p => toMoneyCents(p.vPag) > 0);

        let totalPago = 0;
        const clienteEhConsumidorPadrao = Number(nota?.clienteId) === 1;
        const clienteValidoParaPagamentoAPrazo = Boolean(nota?.clienteId) && !clienteEhConsumidorPadrao;
        const formaPagamentoAPrazo = pagamentosValidos
            .map((notaPag) => {
                const formaPagCadastrada = formasPag.find(
                    (item) => Number(item.id) === Number(notaPag?.formaPagId)
                );

                return notaPag?.formaPag?.meioPag != null
                    ? notaPag.formaPag
                    : formaPagCadastrada;
            })
            .find((formaPag) => [MeioPagamento.Carne, MeioPagamento.Fiado]
                .includes(Number(formaPag?.meioPag)));

        const possuiFrete = parseCurrency(nota?.vFrete) > 0;
        totalPago = calcularTotalPagoPDV(pagamentosValidos);

        const pagamentoPrazoInvalido = pagamentosValidos.find((pagamento) => {
            const formaPag = pagamento?.formaPag?.id
                ? pagamento.formaPag
                : formasPag.find((item) => Number(item.id) === Number(pagamento?.formaPagId));

            return !NotaPag.condicoesValidas({ ...pagamento, formaPag });
        });

        if (pagamentoPrazoInvalido) {
            showMessage('Informe a quantidade de parcelas e a data do primeiro vencimento do pagamento a prazo.', 'warning');
            return;
        }

        //Validações
        // O antigo acréscimo artificial de 0,001 foi substituído pela comparação exata em centavos.
        if (!pagamentosValidos.length || pagamentosValidos.some(p => !p.formaPag?.id)) {
            showMessage('Informe uma forma de pagamento válida.', 'warning');
            return;
        }
        if (emissaoNotaProdutoHabilitada && tipoNota === 'NF-e' && !naturezas.some(n => Number(n.id) === Number(nota.naturezaId))) {
            showMessage('Selecione uma natureza válida.', 'warning');
            return;
        }
        if (!clientes.some(c => Number(c.id) === Number(nota.clienteId)) && Number(nota.cliente?.id) !== Number(nota.clienteId)) {
            showMessage('Selecione um cliente válido.', 'warning');
            return;
        }
        if (toMoneyCents(totalPago) !== toMoneyCents(nota.vNota)) {
            showMessage(toMoneyCents(totalPago) < toMoneyCents(nota.vNota)
                ? 'Valor pago insuficiente!' : 'Os pagamentos excedem o valor da venda. Ajuste os lançamentos.', 'warning');
            return;
        } else if (pagamentosValidos.some(p => Number(p.formaPag?.meioPag) === MeioPagamento.Boleto) && clienteEhConsumidorPadrao) {
            showMessage('Por favor, informe um cliente diferente do "Consumidor" para gerar um boleto.', 'warning');
            return;
        } else if (formaPagamentoAPrazo && !clienteValidoParaPagamentoAPrazo) {
            showMessage(`Por favor, informe um cliente diferente do "Consumidor" para forma de pagamento "${formaPagamentoAPrazo.descricao}".`, 'warning');
            return;
        } else if (
            emissaoNotaProdutoHabilitada && tipoNota === "NF-e"
            && classificarItensFiscaisPDV(nota?.notaItens).possuiProdutos
            && clienteEhConsumidorPadrao
        ) {
            showMessage('Por favor, informe um cliente diferente do "Consumidor" para gerar uma NF-e.', 'warning');
            return;
        }

        operacaoRef.current = true;
        setIsGenerating(true);

        try {
            const notaResult = await garantirNotaEPagamentos();
            const notaPagsAtual = Array.isArray(notaResult?.notaPags) ? notaResult.notaPags : [];
            const itensFiscais = classificarItensFiscaisPDV(
                nota?.notaItens?.length ? nota.notaItens : notaResult?.notaItens
            );

            //verifica se existe algum pagamento
            let emitirNF = emissaoNotaProdutoHabilitada && itensFiscais.possuiProdutos
                && deveEmitirNotaFiscalPDV(notaPagsAtual, configTEFSel);

            if (itensFiscais.possuiProdutos && !emitirNF) {
                const resposta = emissaoNotaProdutoHabilitada && await showYesNo("Deseja emitir a "+tipoNota+"?");
                if (!resposta) {
                    if (!itensFiscais.possuiServicos) {
                        // Mesmo um recibo só pode ser concluído depois do processamento dos pagamentos.
                        for (const pagamento of pagamentosRef.current) {
                            if (!pagamento.pago && !await processaPagamentos(pagamento)) return;
                        }
                        await atualizarFinalizacaoNotaPDV({ ...notaResult, situacaoNF: 9 }); //Ajusta i status da nota para recibo

                        const imprimir = !configModuloSel?.perguntaImpressao
                            || await showYesNo('Deseja imprimir o recibo de produto?', 'no');
                        if (imprimir) await processarImpressaoRecibo(notaResult.id, true);
                        await onConfirm({ ...notaAtualRef.current, situacaoNF: 9, notaPags: pagamentosRef.current }, {
                            notaEmitida: true,
                            documentoFiscalEmitido: false
                        });
                        return;
                    }
                } else {
                    emitirNF = true;
                }
            }

            const emitirNFSe = itensFiscais.possuiServicos
                ? await showYesNo("Deseja emitir a nota de serviço (NFS-e)?")
                : false;

            if (emitirNF && possuiFrete && clienteEhConsumidorPadrao) {
                showMessage('Por favor, informe um cliente diferente do "Consumidor" para concluir uma NF com frete.', 'warning');
                return;
            }

            if (emitirNF && possuiFrete && !validarEnderecoClienteParaFrete(notaResult)) {
                return;
            }

            for (const notaPag of pagamentosRef.current) {
                if (!notaPag.pago) {
                    const pagamentosProcessados = await processaPagamentos(notaPag);
                    if (!pagamentosProcessados) { 
                        return; //retorna sem confirmar
                    }
                }
            }        
        
            const emissao = await emitirVendaFiscalPDV({
                notaId: notaResult.id,
                emitirDocumentoProduto: emitirNF,
                modeloDocumentoProduto: tipoNota === "NF-e" ? "55" : "65",
                emitirNFSe,
                aguardarProcessamentoNFSe: true
            });
            if (sessaoRef.current !== sessao || !showRef.current) return;
            if (emitirNF && emissao?.documentoProdutoEmitido !== true) {
                throw new Error('O documento fiscal da venda não foi autorizado.');
            }
            if (emitirNFSe && !emissao?.nfSe) {
                throw new Error('O servidor não retornou o resultado da NFS-e.');
            }
            await processarResultadoEmissaoFiscal(emissao);

            const recibosParaImprimir = [];

            if (!emitirNF && emissao?.notaProdutoId) {
                recibosParaImprimir.push({
                    notaId: emissao.notaProdutoId,
                    descricao: 'de produto'
                });
            }

            if (!emitirNFSe && emissao?.notaServicoId) {
                recibosParaImprimir.push({
                    notaId: emissao.notaServicoId,
                    descricao: 'de serviço'
                });
            }

            const recibosConfirmados = [];
            for (const recibo of recibosParaImprimir) {
                const imprimirRecibo = !configModuloSel?.perguntaImpressao
                    || await showYesNo(`Deseja imprimir o recibo ${recibo.descricao}?`, 'no');

                if (imprimirRecibo) {
                    recibosConfirmados.push(recibo);
                }
            }

            // Publica os eventos somente depois de coletar todas as respostas para
            // o diálogo nativo de impressão não encobrir a segunda confirmação.
            for (const recibo of recibosConfirmados) {
                await processarImpressaoRecibo(recibo.notaId);
            }

            await onConfirm({ ...notaAtualRef.current, notaPags: pagamentosRef.current }, {
                notaEmitida: true,
                documentoFiscalEmitido: emissao?.documentoProdutoEmitido === true || emissao?.nfSe?.autorizada === true,
                protocoloNFSe: emissao?.nfSe?.protocolo
            });
        } catch (error) {
            console.error('[PDV] Erro ao finalizar venda.', {
                notaId: notaAtualRef.current?.id || 0,
                etapa: 'confirmar-venda',
                erro: getErrorMessage(error)
            });
            if (showRef.current && sessaoRef.current === sessao) showMessage(getErrorMessage(error), 'danger');
        } finally {
            if (sessaoRef.current === sessao) {
                operacaoRef.current = false;
                setIsGenerating(false);
                setAguardandoPagamentoTEF(false);
            }
        }
    };

    async function processarImpressaoRecibo(notaId, automaticoJaSolicitado = false) {
        if (automaticoJaSolicitado && configModuloSel?.monitorDesktop && !configModuloSel?.perguntaImpressao) return;
        if (configModuloSel?.monitorDesktop || configModuloSel?.monitorWeb) {
            await solicitarImpressaoReciboMonitorDesktopPDV(notaId);
            return;
        }

        //se não estiver configurado para monitorar a impressão nem desktop nem web, gera o PREVIEW
        await printReciboPDF(notaId);
    }

    const handleBuscarNatureza = async () => {
        const naturezaSel = await handleShowNaturezaSearch();
        if (naturezaSel?.id) {
            handleNaturezaExit(naturezaSel.id, naturezaSel);
        }
    };

    // Função para emitir NFe
    async function processarResultadoEmissaoFiscal(emissao) {
        if (emissao?.documentoProdutoEmitido && emissao?.modeloDocumentoProduto === "55") {
            if (!emissao.danfeNFeBase64) {
                throw new Error("A NF-e foi emitida, mas o DANFE não foi retornado.");
            }
            const bytes = Uint8Array.from(
                atob(emissao.danfeNFeBase64),
                caractere => caractere.charCodeAt(0)
            );
            const urlDanfe = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
            window.open(urlDanfe, '_blank');
        }

        if (emissao?.documentoProdutoEmitido && emissao?.modeloDocumentoProduto === "65") {
            showAlert('NFC-e emitida com sucesso!', 'success');
            const imprimir = !configModuloSel?.perguntaImpressao
                || await showYesNo("Deseja imprimir a NFC-e?", 'no');
            if (imprimir && !configModuloSel?.monitorDesktop && !configModuloSel?.monitorWeb) {
                await printNFCePDFById(emissao.notaProdutoId);
            } else if (
                imprimir
                && (configModuloSel?.monitorDesktop || configModuloSel?.monitorWeb)
                && configModuloSel?.perguntaImpressao
            ) {
                await solicitarImpressaoNFCeMonitorDesktopPDV(emissao.notaProdutoId);
            }
        }

        if (emissao?.nfSe?.autorizada) {
            showAlert(
                `NFS-e emitida com sucesso! Número: ${emissao.nfSe.numeroNFSe}`,
                'success'
            );
            const imprimir = !configModuloSel?.perguntaImpressao
                || await showYesNo("Deseja imprimir o DANFSe?", 'no');
            if (imprimir) {
                if (configModuloSel?.monitorDesktop || configModuloSel?.monitorWeb) {
                    await solicitarImpressaoNFSeMonitorPDV(emissao.notaServicoId);
                } else {
                    await printNFSePDFById(emissao.notaServicoId);
                }
            }
        } else if (emissao?.nfSe?.status === 'AGUARDANDO_PROCESSAMENTO') {
            showMessage(
                `A NFS-e foi recebida e continua em processamento. Protocolo: ${emissao.nfSe.protocolo || 'não informado'}.`,
                'warning'
            );
        } else if (emissao?.nfSe) {
            const detalhe = emissao.nfSe.erros
                || emissao.nfSe.status
                || 'processamento pendente';
            throw new Error(`A NFS-e não foi autorizada: ${detalhe}`);
        }
    }

    // Mantido temporariamente para compatibilidade com o fluxo fiscal anterior.
    // eslint-disable-next-line no-unused-vars
    async function processaNFe(notaId) {
        try {
            const response = await emitirNFePDV(notaId);

            if (!response.base64) {
                console.error("Erro ao emitir NFe:", "Resposta inválida");
                alert("Erro na emissão da NFe.");
                return;
            }

            // Converte Base64 para Blob PDF
            const byteCharacters = atob(response.base64);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const blob = new Blob([byteArray], { type: 'application/pdf' });

            // Abre em nova aba
            const url = URL.createObjectURL(blob);
            window.open(url, '_blank');

            await onConfirm(nota, {
                notaEmitida: true,
                documentoFiscalEmitido: true
            });

        } catch (err) {
            console.error("Erro ao emitir NFe:", err);
            showMessage(getErrorMessage(err), 'danger');            
        }
    }

    // Função para emitir NFCe
    // Mantido temporariamente para compatibilidade com o fluxo fiscal anterior.
    // eslint-disable-next-line no-unused-vars
    async function processaNFCe(notaId) {
        
        try {
            // Emite a NFC-e 
            const response = await emitirNFCePDV(notaId);
            showAlert(`NFC-e emitida com sucesso! Chave: ${response.chave}`, 'success');

            let imprimir = true;

            if (configModuloSel?.perguntaImpressao) {
                imprimir = await showYesNo("Deseja imprimir a NFC-e?", 'no');
            }
            
            if (imprimir) {
                if (configModuloSel?.monitorDesktop || configModuloSel?.monitorWeb) {
                    if (configModuloSel?.perguntaImpressao) {
                        await solicitarImpressaoNFCeMonitorDesktopPDV(notaId);
                    }
                }
                //Só gera preview se nao estiver configurado para monitorar a impressão nem desktop nem web
                else if (!configModuloSel?.monitorWeb) {
                    await printNFCePDFById(notaId);
                }
            }

            //depois da impressao confirma a NFC-e
            await onConfirm(nota, {
                notaEmitida: true,
                documentoFiscalEmitido: true
            });

        } catch (error) {
            showMessage(getErrorMessage(error), 'danger');
            console.error(error);
            return; // Para evitar chamar onConfirm se falhar na emissão
        }        
    }

    // Função para processar os pagamentos antes da emissão da nota
    async function processaPagamentos(notaPag) {
        const sessao = sessaoRef.current;
        const esperaCancelada = () => cancelarEsperaTEFRef.current || sessaoRef.current !== sessao;
        if (notaPag.pago || Number(notaPag.statusTEF) === 2) return true;
        //setNotaPag(notaPag);
        if (notaPag.formaPag?.meioPag === 6) {
            return true;
        } else {
            validarTerminalVenda([notaPag], configTEFSel);
            if (!deveProcessarTEF(notaPag.formaPag, configTEFSel)) { //Sem TEF
                return true;
            }

            if (!configTEFSel?.id) {
                showMessage('Selecione o terminal que processará o pagamento.', 'warning');
                return false;
            }

            const integrador = Number(configTEFSel.integrador);

            if (integrador === IntegradorTEFEnum.Manual) { //Modo Manual
                //await informarDadosTef();
                aplicarEstadoPagamentos(pagamentosRef.current, notaPag);
                let result = await handlePagamento();                
                return result; 

    // Comentários preservados da rotina anterior:
            // // Processa pagamento TEF
            //Rotinas Super TEF
            //Aprovado
            //Cancelado
            // Para evitar chamar onConfirm se falhar na emissão
            } else if (integrador === IntegradorTEFEnum.SuperTef) { //SuperTEF
                cancelarEsperaTEFRef.current = false;
                setAguardandoPagamentoTEF(true);
                try {
                    if (!pagamentoBloqueiaTerminal(notaPag)) {
                        const retorno = await solicitarPagamentoPOSPDV({ notaPagId: notaPag.id, configTEFId: configTEFSel.id });
                        if (esperaCancelada()) return false;
                        if (!retorno?.notaPag?.id) throw new Error('Não foi possível registrar a cobrança SuperTEF.');
                    }
                    registrarStatusTef(notaPag, 1);
                    const resultado = await aguardarPagamentoPOSPDV({ notaPagId: notaPag.id,
                        deveCancelar: esperaCancelada });
                    if (esperaCancelada()) return false;
                    if (resultado.status === 'aprovado' || resultado.status === 'rejeitado') {
                        const aprovado = resultado.status === 'aprovado';
                        registrarStatusTef(notaPag, aprovado ? 2 : 3);
                        const consultados = await fetchNotaPags(notaPag.notaId, { atualizarEstado: false });
                        if (esperaCancelada()) return false;
                        sincronizarPagamentos(consultados, notaPag);
                        showAlert(resultado.retorno?.message || (aprovado ? 'Pagamento aprovado.' : 'Pagamento rejeitado.'), aprovado ? 'success' : 'danger');
                        return aprovado;
                    }
                    if (resultado.status === 'timeout') showMessage(
                        'A máquina não respondeu em 2 minutos. A venda foi mantida aberta e a cobrança não será reenviada automaticamente.', 'warning');
                    return false;
                } catch (error) {
                    if (!esperaCancelada()) showMessage(getErrorMessage(error), 'danger');
                    return false;
                } finally {
                    if (sessaoRef.current === sessao) setAguardandoPagamentoTEF(false);
                }
            } else if (integrador === IntegradorTEFEnum.MSiTef) { //MSitef
                try {
                    if (!configTEFSel?.id) {
                        showMessage('Selecione o terminal que processará o pagamento.', 'warning');
                        return false;
                    }

                    cancelarEsperaTEFRef.current = false;
                    setAguardandoPagamentoTEF(true);

                    const statusAtual = await buscarStatusPagamentoMonitorTEFPDV(notaPag.id);
                    if (esperaCancelada()) return false;
                    const statusTEFAtual = obterStatusTEFMonitorPDV(statusAtual);
                    console.info('[PDV] Status do pagamento m-SiTef consultado.', {
                        notaId: notaPag.notaId || nota.id,
                        notaPagId: notaPag.id,
                        etapa: 'consultar-status-tef',
                        statusTEF: statusTEFAtual
                    });

                    if (statusTEFAtual === 2 || statusAtual?.pago === true || statusAtual?.Pago === true) {
                        registrarStatusTef(notaPag, 2);
                        return true;
                    }

                    if (deveSolicitarPagamentoMonitorTEF(statusAtual)) {
                        const result = await solicitarPagamentoMonitorTEFPDV({
                            notaPagId: notaPag.id,
                            configTEFId: configTEFSel.id
                        });
                        if (esperaCancelada()) return false;

                        showAlert(result?.message || 'Pagamento enviado para o m-SiTef.');
                    }
                    registrarStatusTef(notaPag, 1);

                    const resultadoEspera = await aguardarPagamentoMonitorTEF({
                        notaPagId: notaPag.id,
                        deveCancelar: esperaCancelada
                    });
                    if (esperaCancelada()) return false;
                    console.info('[PDV] Espera do pagamento m-SiTef finalizada.', {
                        notaId: notaPag.notaId || nota.id,
                        notaPagId: notaPag.id,
                        etapa: 'aguardar-pagamento-tef',
                        resultado: resultadoEspera.status
                    });

                    if (resultadoEspera.status === 'aprovado') {
                        const pagamentosAtualizados = await fetchNotaPags(notaPag.notaId || nota.id, { atualizarEstado: false });
                        if (esperaCancelada()) return false;

                        if (!sincronizarPagamentos(pagamentosAtualizados, notaPag)) {
                            registrarStatusTef(notaPag, 2);
                        }

                        showAlert('Pagamento TEF aprovado.');
                        return true;
                    }

                    if (resultadoEspera.status === 'rejeitado') {
                        registrarStatusTef(notaPag, 3);
                        const pagamentosAtualizados = await fetchNotaPags(notaPag.notaId || nota.id, { atualizarEstado: false });
                        if (esperaCancelada()) return false;
                        sincronizarPagamentos(pagamentosAtualizados, notaPag);
                        showAlert(
                            resultadoEspera.retorno?.message ||
                            resultadoEspera.retorno?.Message ||
                            'Pagamento TEF cancelado ou rejeitado.',
                            'danger'
                        );
                        return false;
                    }

                    if (resultadoEspera.status === 'timeout') {
                        showMessage(
                            'A máquina não respondeu em 2 minutos. A venda foi mantida aberta e a cobrança não será reenviada automaticamente.',
                            'warning'
                        );
                    }

                    return false;
                } catch (error) {
                    if (!esperaCancelada()) {
                        showMessage(error.message || 'Erro ao solicitar pagamento no MSitef.', 'danger');
                    }
                    console.error(error);
                    return false;
                } finally {
                    if (!esperaCancelada()) {
                        setAguardandoPagamentoTEF(false);
                    }
                }
            } else {
                showMessage('O integrador selecionado ainda não está disponível para pagamentos.', 'warning');
                return false;
            }
        }        
    }

    // Comentários preservados da rotina anterior:
    // Se o valor recebido for menor que o valor total da nota
    //Pega o total já pago
    // Garante que valores nulos sejam tratados como 0
    //Se o  for menor ou igual que o valor total da nota
    //Ajusta o valor de pagamento automático
    //Se o valor recebido for maior que o valor total da nota
    //Ajusta o valor de pagamento automático
    // Atualiza a `notaPag` selecionada
    // ✅ Dinheiro já vai pago
    // ✅ idem
    //se a notapag já existir atualiza no banco
    // Atualiza a última entrada em `notaPags`
    // preserva ou define
    //Valida se terminou de pagar a nota
    // ✅ Define apenas no novo
    // ✅ Não reindexa tudo
    //setVRecebido(0);
    const handleExitValorRecebido = (valRecebido, selectedFormaPag, abrirRascunho = true) => {
        const selecionado = pagamentoSelecionadoRef.current;
        if (!selecionado || pagamentoBloqueiaTerminal(selecionado)) return pagamentosRef.current;
        try {
            const forma = selectedFormaPag || selecionado.formaPag;
            if (!forma?.id) throw new Error('Selecione uma forma de pagamento válida.');
            const atualizado = aplicarCondicoesPagamento(NotaPag.calcularRecebimento({
                pagamento: selecionado, formaPag: forma, recebido: parseNumeric(valRecebido),
                total: notaAtualRef.current.vNota, pagamentos: pagamentosRef.current
            }), forma);
            let pagamentos = pagamentosRef.current.map(pagamento =>
                NotaPag.mesmoPagamento(pagamento, selecionado) ? atualizado : pagamento);
            let proximo = atualizado;
            if (abrirRascunho && toMoneyCents(atualizado.vPag) > 0
                && toMoneyCents(calcularTotalPagoPDV(pagamentos)) < toMoneyCents(notaAtualRef.current.vNota)) {
                proximo = pagamentos.find(p => !p.id && toMoneyCents(p.vPag) === 0
                    && !NotaPag.mesmoPagamento(p, atualizado)) || novaNotaPag(NotaPag.proximoItem(pagamentos));
                if (!pagamentos.includes(proximo)) pagamentos = [...pagamentos, proximo];
            }
            aplicarEstadoPagamentos(pagamentos, proximo);
            return pagamentos;
        } catch (error) {
            showMessage(error.message, 'warning');
            atualizarRecebido(formatarValorInput(selecionado.vRec));
            return null;
        }
    };

    const handleClienteExit = (clienteId, clienteFallback = null) => {
        if (!clienteId) {
            setNota(atual => inicializarClienteFinalizacaoPDV({
                nota: { ...atual, cliente: null, clienteId: '', clienteNome: '', cpf: '', cnpj: '' }, clientes
            }));
            return;
        }
        //const clienteSelecionado = clientes.find(f => f.id.toString() === clienteId.toString()) || clienteFallback;
        const clienteSelecionado = clienteFallback || clientes.find(f => f.id.toString() === clienteId.toString()) ;
        if (clienteSelecionado) {
            setNota(atual => ({ ...atual, cliente: clienteSelecionado, clienteId: clienteSelecionado.id, clienteNome: clienteSelecionado.pessoa?.nome,
                cpf: clienteSelecionado.pessoa?.fisica?.cpf || '',
                cnpj: clienteSelecionado.pessoa?.juridica?.cnpj || '' }));
        } else {
            showAlert('Cliente não encontrado!', 'danger');
            setNota(atual => ({ ...atual, cliente: null, clienteId: '', clienteNome: '', cpf: '', cnpj: '' }));
        }
    };

    const handleNaturezaExit = (naturezaId, naturezaFallback = null) => {
        if (!naturezaId) { setNota(atual => ({ ...atual, naturezaId: '', natureza: null })); return; }

        const naturezaSelecionada = naturezaFallback || naturezas.find(n => n.id.toString() === naturezaId.toString());
        if (naturezaSelecionada) {
            setNota(atual => ({
                ...atual,
                naturezaId: naturezaSelecionada.id,
                natureza: naturezaSelecionada
            }));
        } else {
            showAlert('Natureza não encontrada!', 'danger');
            setNota(atual => ({ ...atual, naturezaId: '', natureza: null }));
        }
    };

    const handleFreteChange = (value) => {
        if (operacaoRef.current) return;
        const normalizado = normalizeDecimalInputBr(value.includes(',') ? value.replace(/\./g, '') : value);
        const vFrete = normalizeMoney(parseNumeric(normalizado));
        if (vFrete < 0) { showMessage('O frete não pode ser negativo.', 'warning'); return; }

        setVFreteInput(normalizado || null);
        setNota((notaAtual) => ({
            ...notaAtual,
            vFrete,
            vNota: calcularValorNota(notaAtual, vFrete)
        }));
    };

    const handleFreteBlur = (value) => {
        if (operacaoRef.current) return;
        const vFrete = normalizeMoney(parseNumeric(value));
        if (vFrete < 0) { showMessage('O frete não pode ser negativo.', 'warning'); return; }
        const freteFormatado = formatarValorInput(vFrete);

        setVFreteInput(freteFormatado);
        setNota((notaAtual) => ({
            ...notaAtual,
            vFrete,
            vNota: calcularValorNota(notaAtual, vFrete)
        }));
        const selecionado = pagamentoSelecionadoRef.current;
        const notaAtual = notaAtualRef.current;
        notaAtualRef.current = { ...notaAtual, vFrete, vNota: calcularValorNota(notaAtual, vFrete) };
        if (selecionado?._valorAutomatico && !pagamentoBloqueiaTerminal(selecionado)) {
            handleExitValorRecebido(fromMoneyCents(NotaPag.saldoCentavos(notaAtualRef.current.vNota,
                pagamentosRef.current, selecionado)), undefined, false);
        }
    };

    // Comentários preservados da rotina anterior:
    //Ajusta o valor de pagamento automático
    //setVRecebido(parseCurrency(vTroco)*-1);
    // Garante que valores nulos sejam tratados como 0
    //ajuste para que notas já pagas fiquem com o status pago
    //setVTroco(0);
    //if (vRecebido=)
    const handleSelectPag = (formaPagId) => {
        const selecionado = pagamentoSelecionadoRef.current;
        if (operacaoRef.current || pagamentoBloqueiaTerminal(selecionado)) return;
        const forma = formasPag.find(f => Number(f.id) === Number(formaPagId) && !f.inativo);
        if (!forma) { showMessage('Selecione uma forma de pagamento válida.', 'warning'); return; }
        const atualizado = aplicarCondicoesPagamento({ ...selecionado, formaPagId: forma.id,
            formaPag: forma, _valorAutomatico: true, qtdeParcela: null,
            dataVencimento: obterPrimeiroVencimentoPadrao(forma) }, forma);
        aplicarEstadoPagamentos(pagamentosRef.current.map(p =>
            NotaPag.mesmoPagamento(p, selecionado) ? atualizado : p), atualizado);
        const saldo = fromMoneyCents(NotaPag.saldoCentavos(notaAtualRef.current.vNota, pagamentosRef.current, atualizado));
        handleExitValorRecebido(saldo, forma, false);
    };

    const handleExcluirPagamento = async (pagamento) => {
        if (operacaoRef.current || pagamentoBloqueiaTerminal(pagamento)) return;
        const sessao = sessaoRef.current;
        const resposta = await showYesNo('Deseja excluir a forma de pagamento selecionada?');
        if (!resposta || operacaoRef.current || sessaoRef.current !== sessao) return;
        operacaoRef.current = true;
        setIsGenerating(true);
        try {
            if (pagamento.id && !await deleteNotaPag(pagamento.id, { atualizarEstado: false })) return;
            if (sessaoRef.current !== sessao) return;
            let pagamentos = pagamentosRef.current.filter(p => !NotaPag.mesmoPagamento(p, pagamento));
            let selecionado = pagamentos[pagamentos.length - 1];
            if (!pagamentos.length || toMoneyCents(calcularTotalPagoPDV(pagamentos)) < toMoneyCents(notaAtualRef.current.vNota)) {
                selecionado = pagamentos.find(p => !p.id && toMoneyCents(p.vPag) === 0)
                    || novaNotaPag(NotaPag.proximoItem(pagamentos));
                if (!pagamentos.includes(selecionado)) pagamentos = [...pagamentos, selecionado];
            }
            aplicarEstadoPagamentos(pagamentos, selecionado);
        } finally {
            if (sessaoRef.current === sessao) {
                operacaoRef.current = false;
                setIsGenerating(false);
            }
        }
    };


    const processarPagamento = async (pag) => {
        if (operacaoRef.current || pag.pago) return;
        const sessao = sessaoRef.current;
        if (!handleExitValorRecebido(recebidoRef.current, undefined, false)) return;
        if (toMoneyCents(pagamentosRef.current.find(p => NotaPag.mesmoPagamento(p, pag))?.vPag) <= 0) {
            showMessage('Informe um valor de pagamento maior que zero.', 'warning');
            return;
        }
        try {
            operacaoRef.current = true;
            setIsGenerating(true);
            const notaResult = await garantirNotaEPagamentos();
            const pagAtual = notaResult.notaPags.find(p =>
                pag.id ? p.id === pag.id : p.item === pag.item
            );

            // 3. Agora processa o pagamento normalmente
            if (!pagAtual) {
                throw new Error('Pagamento não encontrado após a persistência.');
            }
            await processaPagamentos(pagAtual);
            // const result = await processaPagamentos(pagAtual);

            // if (result) {
            // // marca como pago
            // setNotaPags(prev =>
            //     prev.map(p =>
            //     p === pagAtual ? { ...p, pago: true } : p
            //     )
            // );
            // showAlert(
            //     `Pagamento ${pagAtual.formaPag?.descricao} processado com sucesso!`,
            //     "success"
            // );
            // }
        } catch (err) {
            console.error("Erro ao processar pagamento:", err);
            if (sessaoRef.current === sessao && showRef.current) showAlert(getErrorMessage(err) || "Erro ao processar pagamento!", "danger");
        } finally {
            if (sessaoRef.current === sessao) {
                operacaoRef.current = false;
                setIsGenerating(false);
                setAguardandoPagamentoTEF(false);
            }
        }
    };



    if (!show) return null;

    return (
        <ModalContainer show={show} variant="cadastro" onHide={() => { if (!operacaoRef.current) onHide(); }} largura="840px" contentClassName="finalizacao-venda-modal modal-operacional">
                    <ModalHeader title="Finalizar Venda" icon={FiDollarSign} onHide={() => { if (!operacaoRef.current) onHide(); }} closeDisabled={isGenerating}>
                            <div className="tipo-nota-buttons" role="group" aria-label="Tipo de documento fiscal">
                            <button
                                type="button" aria-pressed={tipoNota === 'NFC-e'}
                                disabled={isGenerating}
                                className={`tipo-nota-button ${tipoNota === 'NFC-e' ? 'active' : ''}`}
                                onClick={() => handleSelecionarTipoNota('NFC-e')}
                            >
                                NFC-e
                            </button>
                            <button
                                type="button" aria-pressed={tipoNota === 'NF-e'}
                                disabled={isGenerating}
                                className={`tipo-nota-button ${tipoNota === 'NF-e' ? 'active' : ''}`}
                                onClick={() => handleSelecionarTipoNota('NF-e')}
                            >
                                NF-e
                            </button>
                            </div>
                    </ModalHeader>
                    <ModalBody>
                    <div className="finalizacao-grid">
                        {/* Lado Esquerdo */}
                        <div className="finalizacao-dados">
                            <div className="finalizacao-section-header">
                              <h6><FiUser aria-hidden="true" />Cliente e documento</h6>
                              {permitirEditarItens && (
                                <button
                                    type="button"
                                    className="btn btn-outline-secondary modal-action finalizacao-editar-itens"
                                    onClick={handleAbrirEditarItens}
                                    disabled={isGenerating || Boolean(nota?.id)}
                                    title={nota?.id
                                        ? 'Os itens não podem mais ser editados após o início do pagamento.'
                                        : 'Editar produtos, quantidade e valor dos itens da nota'}
                                >
                                    <FaEdit aria-hidden="true" />
                                    Editar Itens
                                </button>
                              )}
                            </div>
                            <div className="finalizacao-row">
                                <Input
                                    label="Cliente"
                                    disabled={isGenerating}
                                    value={nota.clienteId || ''}
                                    onChange={(e) => setNota({ ...nota, clienteId: e.target.value })}
                                    onBlur={(e) => handleClienteExit(e.target.value)}
                                    btnBuscar={handleBuscarCliente}
                                    btnAdd={handleAddCliente}
                                    btnBuscarDisabled={isGenerating}
                                    btnAddDisabled={isGenerating}
                                    size={80}
                                />
                                <Input
                                    label="Descrição"
                                    value={nota?.clienteNome}
                                    disabled={isGenerating}
                                    onChange={(e) => setNota({ ...nota, clienteNome: e.target.value })}
                                    size={250}
                                />
                            </div>
                            <div className="finalizacao-row finalizacao-identificacao">
                                <Input
                                    label="CPF"
                                    disabled={isGenerating}
                                    value={nota?.cpf}
                                    onChange={(e) => setNota({ ...nota, cpf: e.target.value })}
                                    size={130}
                                    mask={'999.999.999-99'}
                                />
                                <Input
                                    label="CNPJ"
                                    disabled={isGenerating}
                                    value={nota?.cnpj}
                                    onChange={(e) => setNota({ ...nota, cnpj: e.target.value })}
                                    size={170}
                                    mask={'99.999.999/9999-99'}                                    
                                />
                                <Input
                                    label="Frete"
                                    type="text"
                                    inputMode="decimal"
                                    disabled={isGenerating}
                                    value={vFreteInput ?? ''}
                                    placeholder="0,00"
                                    onChange={(e) => handleFreteChange(e.target.value)}
                                    onBlur={(e) => handleFreteBlur(e.target.value)}
                                    size={110}
                                    style={{ textAlign: 'right' }}
                                />
                            </div>

                            {tipoNota === 'NF-e' && (
                                <div className="finalizacao-row">
                                    <Input
                                        label="Natureza"
                                        disabled={isGenerating}
                                        value={nota.naturezaId || ''}
                                        onChange={(e) => setNota({ ...nota, naturezaId: e.target.value })}
                                        onBlur={(e) => handleNaturezaExit(e.target.value)}
                                        btnBuscar={handleBuscarNatureza}
                                        btnBuscarDisabled={isGenerating}
                                        size={80}
                                    />
                                    <Input
                                        label="Descrição"
                                        value={nota?.natureza?.descricao || ''}
                                        disabled={isGenerating}
                                        onChange={(e) => setNota({
                                            ...nota,
                                            natureza: {
                                                ...(nota?.natureza || {}),
                                                descricao: e.target.value
                                            }
                                        })}
                                        size={250}
                                    />
                                </div>
                            )}

                            <div className="finalizacao-row">
                                <Input
                                    label="Observação"
                                    disabled={isGenerating}
                                    value={nota?.observacao}
                                    onChange={(e) => setNota({ ...nota, observacao: e.target.value })}
                                    size={450}                             
                                />
                            </div>  

                            {/* Grid de NotaPags */}
                            <div className="finalizacao-pagamentos">
                            <h6><FiList aria-hidden="true" />Pagamentos</h6>
                            <ModalTable className="table-sm-custom" emptyMessage="Nenhum pagamento informado.">
                                <thead>
                                <tr>
                                    <th style={{ width: '20px' }}>#</th>
                                    <th>Forma Pág.</th>
                                    <th className="finalizacao-valor-col">Valor</th>
                                    <th className="finalizacao-status-col">Status</th>
                                    <th className="finalizacao-cobranca-col">Cobrança</th>
                                    <th className="finalizacao-excluir-col"><span className="visually-hidden">Excluir</span></th>
                                </tr>
                                </thead>
                                <tbody>
                                {notaPags && notaPags.map((pag, index) => {
                                    const podeCobrar = deveProcessarTEF(pag?.formaPag, configTEFSel);
                                    return (
                                    <tr key={pag.id || `rascunho-${pag.item}`}>
                                        <td>{pag.item}</td>
                                        <td>{pag?.formaPag?.descricao || '-'}</td>
                                        <td style={{textAlign: 'right'}}>{pag.vPag ? formatCurrency(pag.vPag) : '-'}</td>
                                        <td className="text-center">
                                        {pag.pago ? (
                                            <span className="badge bg-success">Pago</span>
                                        ) : (
                                            <span className="badge bg-warning text-dark">Pendente</span>
                                        )}
                                        </td>

                                        <td className="text-center">
                                        {podeCobrar && (
                                            <button
                                            disabled={isGenerating || pag.pago || toMoneyCents(pag.vPag) <= 0} // ← aqui
                                            type="button"
                                            aria-label={`Enviar pagamento ${pag?.formaPag?.descricao || "selecionado"}`}
                                            className="btn btn-outline-primary btn-mini"
                                            // style={{padding:'0px'}}
                                            onClick={() => processarPagamento(pag)}
                                            >
                                            {Number(pag?.formaPag?.meioPag) === MeioPagamento.PIX && <FaMobileAlt aria-hidden="true" />}
                                            {[MeioPagamento.CartaoCredito, MeioPagamento.CartaoDebito].includes(Number(pag?.formaPag?.meioPag)) && <FaCreditCard aria-hidden="true" />}
                                            {Number(pag?.formaPag?.meioPag) === MeioPagamento.TEF_Manual && <FaKeyboard aria-hidden="true" />}
                                            <span className="finalizacao-envio-text">Enviar</span>
                                            </button>
                                        )}
                                        </td>
                                        <td className="text-center">
                                            <button
                                                type="button"
                                                className="btn btn-outline-danger btn-mini"
                                                title="Excluir forma de pagamento"
                                                aria-label={`Excluir pagamento ${pag?.formaPag?.descricao || "selecionado"}`}
                                                onClick={() => handleExcluirPagamento(pag, index)}
                                                disabled={isGenerating || pagamentoBloqueiaTerminal(pag)}
                                            >
                                                <FaTrashAlt />
                                            </button>
                                        </td>
                                    </tr>
                                    );
                                })}
                                </tbody>
                            </ModalTable>
                            </div>                    
                        </div>

                        {/* Lado Direito */}
                        <div className="finalizacao-valores">
                            <h6><FaCreditCard aria-hidden="true" />Pagamento</h6>
                            {/* <button onClick={() => setNotaPag({...notaPag, formaPagId:1})}>teste</button> */}
                            <div className="finalizacao-condicoes">
                                <div className="finalizacao-row">
                                <Select
                                    label="Forma Pág."
                                    disabled={isGenerating || pagamentoBloqueiaTerminal(notaPagSel)}
                                    value={notaPagSel.formaPagId || ''}
                                    onChange={(e) => handleSelectPag(e.target.value)}
                                    options={formasPag
                                        .filter(fp => !fp.inativo) // Filtra apenas os itens ativos
                                        .map(fp => ({ value: fp.id, label: fp.descricao }))
                                      }
                                    size="100%"
                                    autoFocus={true}
                                />                                                                
                                </div>  
                                {formaPagSel?.id && formaPagSel.avista !== true && (
                                    <div className="finalizacao-row">
                                        <Select
                                            label="Parcelas"
                                            disabled={isGenerating || pagamentoBloqueiaTerminal(notaPagSel)}
                                            value={parseInt(notaPagSel.qtdeParcela)}
                                            onChange={(e) => atualizarCondicoesPagamento({
                                                qtdeParcela: e.target.value === '' ? null : Number(e.target.value)
                                            })}

                                            options={[
                                                { value: 1, label: '1x' },
                                                { value: 2, label: '2x' },
                                                { value: 3, label: '3x' },
                                                { value: 4, label: '4x' },
                                                { value: 5, label: '5x' },
                                                { value: 6, label: '6x' },
                                                { value: 7, label: '7x' },
                                                { value: 8, label: '8x' },
                                                { value: 9, label: '9x' },
                                                { value: 10, label: '10x' },
                                                { value: 11, label: '11x' },
                                                { value: 12, label: '12x' },
                                            ]}
                                            size={65}
                                        />
                                        <Input
                                            label="1º vencimento"
                                            disabled={isGenerating || pagamentoBloqueiaTerminal(notaPagSel)}
                                            type="date"
                                            value={formatarDataInput(notaPagSel.dataVencimento)}
                                            onChange={(e) => atualizarCondicoesPagamento({ dataVencimento: e.target.value })}
                                            size={115}
                                        />
                                    </div>
                                )}
                                {mostraTerminal && (
                                    <Select
                                        label="Terminal"
                                        value={configTEFSel?.id}
                                        disabled={isGenerating || terminalVendaBloqueado(notaPags)}
                                        onChange={(e) => {                                            
                                            if (isGenerating || terminalVendaBloqueado(notaPags)) return;
                                            const configTEF = configTEFList.find(c => c.id === parseInt(e.target.value));
                                            setConfigTEFSel(configTEF);
                                            //setNota({ ...nota, terminalId: e.target.value });
                                        }}
                                        options={configTEFList
                                            //.filter(ct => !ct.inativo) // Filtra apenas os itens ativos
                                            .filter(integradorTefDisponivelPDV)
                                            .map(ct => ({ value: ct.id, label: ct.nomeTerminal }))
                                            }
                                        size="100%"
                                        autoFocus={true}
                                    />                                    
                                )}                              
                            </div>                            

                            <div className="finalizacao-row finalizacao-total">
                                <Input
                                    label={<><FiDollarSign aria-hidden="true" />Valor Total</>}
                                    value={formatCurrency(nota.vNota) || ''}
                                    size="100%"
                                    disabled={true}
                                    style={{ fontWeight: 600, fontSize: '20px', textAlign: 'right' }}
                                    //onChange={(e) => setNota({ ...nota, vNota: e.target.value })}
                                />
                                
                            </div>
                            <div className="finalizacao-row finalizacao-recebido">
                                <Input
                                    label={<><FiArrowDownLeft aria-hidden="true" />Valor Recebido</>}
                                    value={vRecebido ?? ''}
                                    placeholder="0,00"
                                    size="100%"
                                    type="text"
                                    inputMode="decimal"
                                    disabled={isGenerating || pagamentoBloqueiaTerminal(notaPagSel)}
                                    onChange={(e) => {
                                        if (operacaoRef.current) return;
                                        const valor = e.target.value;
                                        atualizarRecebido(normalizeDecimalInputBr(valor.includes(',') ? valor.replace(/\./g, '') : valor));
                                        if (pagamentoSelecionadoRef.current) pagamentoSelecionadoRef.current = { ...pagamentoSelecionadoRef.current, _valorAutomatico: false };
                                    }}
                                    style={{ fontWeight: 600, fontSize: '20px', textAlign: 'right' }}
                                    onBlur={(e) => handleExitValorRecebido(e.target.value,formaPagSel)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Tab' && !e.shiftKey && confirmarRef.current && !confirmarRef.current.disabled) {
                                            e.preventDefault();
                                            confirmarRef.current.focus();
                                        }
                                    }}
                                />
                            </div>
                            <div className="finalizacao-row finalizacao-troco">
                                <Input
                                    label={<><FiCornerUpLeft aria-hidden="true" />Troco</>}
                                    value={formatCurrency(vTroco) || ''}
                                    size="100%"
                                    disabled={true}
                                    style={{ fontWeight: 600, fontSize: '20px', textAlign: 'right' }}
                                    //onChange={(e) => setNota({ ...nota, vRecebido: e.target.value })}
                                />
                            </div>
                        </div>
                    </div>
                    </ModalBody>
                    <ModalFooter>
                        {/* Desabilitar o botão Confirmar enquanto o boleto está sendo gerado */}
                        {isGenerating && (
                            <div className="finalizacao-processando" role="status" aria-live="polite">
                                <span className="ms-2">
                                    {aguardandoPagamentoTEF
                                        ? `Aguardando pagamento na máquina ${configTEFSel?.nomeTerminal || ''}...`
                                        : `Aguarde, processando ${notaPagSel.formaPag?.descricao || ''}...`}
                                </span>
                                <div className="spinner-border spinner-border-sm text-primary" aria-hidden="true">
                                </div>                                
                            </div>
                        )}
                        <ModalActionButton ref={confirmarRef} action="confirm" onClick={handleConfirm}
                            disabled={isGenerating} // Desabilita o botão se estiver gerando
                        >Confirmar</ModalActionButton>
                        <ModalActionButton action="close" onClick={() => { if (!operacaoRef.current) onHide(); }} disabled={isGenerating}>Fechar</ModalActionButton>
                    </ModalFooter>
            <ModalTefManual
                show={showModal}
                configTEF={configTEFSel}
                onResolve={(confirmed, pagamentos) => onResolveRef.current(confirmed, pagamentos)}
                //notaPag={notaPag}
            />
            {permitirEditarItens && (
                <ModalEditarItensNota
                    show={showEditarItens}
                    nota={nota}
                    onSalvar={handleSalvarItensEditados}
                    onClose={handleFecharEditarItens}
                />
            )}
        </ModalContainer>
    );
};

export default ModalFinalizarVenda;
